/**
 * My day (3.2–3.3). One of My week's day columns blown up into a real day: a
 * vertical time axis over the clinician's own pattern, stops sized by their
 * planned duration, and the drive between them drawn from the visit resources'
 * own minutes, in the same column as the stops.
 *
 * It opens **over** My week rather than instead of it (Pete, 2026-09-19: *"can we
 * try the day view as a pop-over panel vs. a completely separate page"*), as a
 * `Dialog` — the same overlay `RouteDayDialog` and `SequenceDayDialog` already
 * use, so the day, its route and its proposed order all arrive the same way. The
 * date and the way out are props: My week still carries the URL, so the back
 * button and a deep link still work.
 *
 * **The frame is anchored to the viewport and only the axis scrolls** (Pete,
 * 2026-09-19: *"the op is anchored off screen … I'd like the framework around the
 * calendar view to be anchored in the view port and then to only scroll within the
 * calendar so the header is always visible"*). `scroll="paper"` alone was not
 * enough — the title row went off the top of the screen on Pete's machine — and
 * **why** it was not enough has never been reproduced here: MUI's own
 * `paperScrollPaper` caps the paper at `calc(100% - 64px)` and `DialogContent` is
 * already a scroll container, so neither the cap nor the flex min-size should have
 * failed on paper. The frame is therefore stated outright rather than inferred:
 * the paper is given an explicit `calc(100vh - 64px)` — the viewport less MUI's
 * own 32px margin at each end — as a flex column, and the body is `flex: 1 1 0`
 * with `minHeight: 0` and its own scrollbar. That holds whatever was pushing the
 * title off. The title row, its controls and the day menu are outside the scroller
 * and never move.
 *
 * It is not a new store, a new engine or a scheduler. It reads what My week
 * reads (`selectMyDay`, built on `selectMyWeek`) and writes what My week writes
 * (`moveVisitOptimistically`, the product's own add-block and cancel dialogs).
 * The footer's two numbers come from the week column's own
 * day, so the two views cannot disagree about a day.
 *
 * Three things the axis does that the week grid cannot:
 *
 * - **It shows a hole.** A hole is the point of this view, and it is shown by
 *   being empty. The axis used to draw a dotted band labelled "Open 12:45–14:15"
 *   over every open span; Pete took them off on 2026-09-18 — *"the empty space is
 *   clue enough"* — and the stretch of blank grid between two stops is now the
 *   whole of it.
 * - **It lets "later" be somewhere.** A visit drags up and down, snaps to the
 *   grid, and **so does a block** (Pete, 2026-09-18). A drop onto another stop's
 *   span **lands** (Pete, 2026-09-19): the strip names what it runs into while the
 *   card is held, and the lanes draw the two side by side. Refusing it made a day
 *   impossible to shuffle, and was the one place these guardrails blocked instead
 *   of informing.
 * - **It draws the drive the staged day would actually drive.** Every re-time is
 *   priced the moment it is staged, on the product's own drive-time engine, and
 *   the answer is drawn as an ordinary solid band with its minutes on it — the
 *   same treatment a segment nobody has touched wears (Pete, 2026-09-19).
 *   `GetSimpleDriveTime` is a **query**, so this costs a paid Google call and
 *   writes nothing, which is what lets the number arrive on the drop while the
 *   write still waits for Save. A segment is dashed with no minutes only when a
 *   real write is in flight and the overlay is carrying the old minutes until the
 *   poll lands.
 *
 * **A drop is a proposal; Save is the write** (Pete, 2026-09-19: *"I'd like there
 * to be one save button that saves all pending changes instead of it happening on
 * each drop. Should also be a reset to undo all pending changes"*). Every re-time
 * on the axis is staged in this component's own memory and drawn at once; the
 * header says how many are waiting, **Save** writes them all through
 * `saveDayRetimes` — the same mutations and one drive reprice for the batch — and **Reset** throws them away. Nothing already saved is
 * touched by either.
 *
 * The staged day is drawn by rewriting the horizon's own rows — see
 * `stagedState` — with the staged hours and the priced drive minutes, which is
 * why nothing downstream had to learn about staging: `selectMyDay` lays the card
 * out at its staged hour, draws its drive as a plain number, and the overlap the
 * strip names is measured against the other staged cards. It is rebuilt from the
 * real state on every change and never dispatched, so the overlay's sixty-second TTL
 * never runs against it — a change staged and talked over for two minutes is
 * still there — and a write genuinely in flight still reads as one, because the
 * real overlay is applied on top.
 *
 * Only a **re-time** is staged. Cancelling a visit, removing a block, **Block
 * time**, **Optimize** and the route view all still write when they are chosen.
 * **Optimize** is the one place that line is hard to hold: it reads the day off
 * the server and `acceptSequence` writes straight over it, so it would silently
 * fight the staged hours. It is disabled while anything is pending.
 *
 * A drive band is drawn at the **full width of its stop's lane**, through the
 * same `laneBox` the card uses, because the drive is part of that stop's
 * footprint — the product's own calendar carries it as the event's `bufferBefore`
 * and `bufferAfter` and lets the scheduler stack a colliding buffer side by side
 * (`apps/web/src/utils/eventsHelper.ts`). `selectMyDay` assigns lanes over those
 * footprints, so a drive longer than the hole before its stop lands in a lane
 * beside what it runs into instead of on top of it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack, Tooltip, Typography } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { StatusBgColors, StatusBgColorsDark } from "@caredna-marlin/utils/client";
import AddCalendarBlock from "~/components/command/AddCalendarBlock";
import CancelVisitDialog from "~/components/command/CancelVisitDialog";
import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import { CalendarBlockBlockTypeEnum } from "~/graphql/__generated__/graphql";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { useNow } from "../../components/useNow";
import { addWorkingDaysToKey, dateKeyAt, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import {
  hhmmOfMinutes,
  hourLines,
  MIN_STOP_MINUTES,
  minutesOfHhmm,
  describeOverlap,
  stopsOverlapping,
  selectMyDay,
  snapToGrid,
  type DayStop,
} from "../../engine/dayAxis";
import { selectConfirmationRows } from "../../engine/confirmationRound";
import { ConfirmationIcon } from "../../components/ConfirmationIcon";
import { useBranchClock } from "../../components/branchClock";
import { useCardWindowLabel } from "../../components/useCardWindowLabel";
import { evaluateRealVisitMove, type RealMoveEvaluation } from "../../engine/moveGuard";
import type { WeekCard } from "../../engine/myWeek";
import type { DriveTimePlan, PlannedBlockMove, PlannedMove } from "../../data/driveTimes";
import type { DriveMinutes } from "../../data/mutations";
import { confirmByPhoneNow, priceStagedDay, recordBlockCreated, recordVisitCanceled, removeBlockFromWeek, saveDayRetimes, type StagedRetime } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import type { DateKey, DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { CardMenu } from "./CardMenu";
import { DayMenu } from "./DayMenu";
import { GuardrailStrip } from "./GuardrailStrip";
import { MoveAnywayDialog } from "./MoveAnywayDialog";
import { RouteDayDialog } from "./RouteDayDialog";
import { SequenceDayDialog } from "./SequenceDayDialog";
import { isInspectable, VisitInspectPopover } from "./VisitInspectPopover";

/** How tall a minute is. An eleven-hour pattern is then about 800px: one screen and a little scroll. */
const PX_PER_MINUTE = 1.2;
const GUTTER_PX = 56;
/** The hatch every held and reserved surface on this demo is drawn with. */
const HATCH = "repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0 6px, transparent 6px 12px)";

/**
 * The command centre's own treatments, so a visit, a block and a stretch of drive
 * read the same in both views (Pete, 2026-09-19). Each one is lifted from the code
 * that draws command's timeline rather than guessed at:
 *
 * - a **scheduled visit** is `calculateInfoColor` in `~/utils/statuses`, which is
 *   `StatusBgColors.scheduled` on the fill and `StatusBgColorsDark.scheduled` on a
 *   1px border (`views/command/CalendarEvent/VisitEvent.tsx`);
 * - a **block** is `#e5e5e5` behind a 1px dotted grey border
 *   (`views/command/CalendarEvent/CalendarBlockBlocking.tsx`);
 * - **drive** is Mobiscroll's buffer, which this app draws itself in
 *   `~/utils/mobiscrollEventFormatter` as -45° grey stripes inside a `#d7d7d7` rule.
 *
 * What cannot come across is Mobiscroll's own geometry: command lays a day out
 * left to right and draws the buffer as an open-sided extension of the event box.
 * My day's axis runs top to bottom and gives the drive a box of its own.
 */
const SCHEDULED_VISIT = { bgcolor: StatusBgColors.scheduled, border: `1px solid ${StatusBgColorsDark.scheduled}` };
const BLOCK_SURFACE = { bgcolor: "#e5e5e5", border: "1px dotted grey" };
const DRIVE_STRIPES = "repeating-linear-gradient(-45deg, #f3f3f3, #f3f3f3 11px, #e5e5e5 11px, #e5e5e5 22px)";
const DRIVE_BORDER_COLOR = "#d7d7d7";

/**
 * Just under the product's own calendar popup, which pins itself at 1205
 * (`components/common/calendar/CalendarPopup.tsx`) — below MUI's 1300 for a
 * modal. "Block time" opens that popup from this panel's own day menu, so the
 * panel has to sit beneath it or the popup opens behind the panel. Everything
 * this panel opens in turn — Move anyway, the cancel dialog, Optimize, the route
 * — is a modal of its own at 1300 and still lands in front.
 */
/**
 * Wedged deliberately. The app bar is `theme.zIndex.drawer + 1` = **1201**
 * (`components/layout/AuthenticatedRoot.tsx`), so 1200 put the panel's title row
 * *behind* it and the header read as cut off at the top of the viewport — which is
 * what Pete saw. `CalendarPopup` hardcodes **1205**, and **Block time** opens one
 * from inside this panel, so the panel has to stay below that. 1202 is above the
 * bar and below the popup; anything this panel opens as a modal is 1300 and still
 * lands in front.
 */
const PANEL_Z = { zIndex: 1202 };

/**
 * The panel's own frame, anchored to the viewport. `100vh` less MUI's default
 * 32px dialog margin at each end, fixed rather than capped so the axis always
 * has the same amount of room and the header is always on screen; the paper is a
 * flex column so the title row, the body and nothing else divide it.
 */
const PANEL_PAPER = { height: "calc(100vh - 64px)", maxHeight: "calc(100vh - 64px)", display: "flex", flexDirection: "column" } as const;

/**
 * The only thing that scrolls. `minHeight: 0` is belt and braces rather than the
 * diagnosis — a flex item that is already a scroll container has an automatic
 * min-size of zero — and it is stated so that a body holding an eleven-hour axis
 * cannot refuse to shrink whatever else changes around it.
 */
const PANEL_BODY = { display: "flex", flexDirection: "column", gap: 2, flex: "1 1 0", minHeight: 0, overflowY: "auto" } as const;

/**
 * Where one thing sits on the axis. A card and the drive that belongs to it are
 * placed through the same function, so a drive band is exactly as wide as the
 * card it arrives at rather than a narrower strip threaded past it — this is what
 * Pete meant by *"the drive time boxes aren't wide enough"* (2026-09-18).
 *
 * Inline rather than `sx` because these four numbers change with every pixel of
 * every drag; emotion would mint a class per value, and a test can read a plain
 * style back.
 */
function laneBox(lane: number, laneCount: number, top: number, height: number): { top: string; height: string; left: string; width: string } {
  return { top: `${top}px`, height: `${height}px`, left: `${(100 / laneCount) * lane}%`, width: `${100 / laneCount}%` };
}

/**
 * One re-time waiting for **Save**, keyed by the card it belongs to. `serverStart`
 * is where the card sits for the server, captured the first time it is picked up,
 * so dragging it back to that minute clears the entry rather than staging a move
 * to nowhere.
 */
interface Staged extends StagedRetime {
  cardId: string;
  serverStart: number;
}

/**
 * The day as it would be if the staged changes were real, with the drive it would
 * actually drive.
 *
 * It is built by rewriting the **horizon rows themselves** — a visit resource's
 * start, end and three drive minutes; a block's start and end — rather than by
 * laying an optimistic overlay over them. Two reasons, and both matter:
 *
 * - **An overlay entry is a promise about a write.** `local` means "sent, waiting
 *   for the server to agree", which is what marks a card `pending` and makes the
 *   drive either side of it dashed with no minutes. A staged change has not been
 *   sent anywhere, and since the day is priced the moment it is staged there is a
 *   real number to draw. Pete, 2026-09-19: *"I still want the drive times updated
 *   each drop and I'd like them to look the same as drive times from visits that
 *   haven't been moved."* Rewriting the rows is what makes them the same.
 * - **`reconcileOverlay` expires an overlay entry the server has not agreed with**
 *   after `LOCAL_OVERLAY_TTL_MS`. A change staged and talked over for a minute
 *   would have evaporated mid-sentence.
 *
 * Nothing here is ever dispatched: it is rebuilt from the real state on every
 * change, and the real `local` overlay is still applied on top of it by
 * `selectEffectiveCalendar`, so a write genuinely in flight still reads as one.
 *
 * `drive` is the priced answer for this arrangement, keyed by **visit resource
 * id** as `DriveTimePlan` gives it — not by visit id.
 */
function stagedState(state: DemoState, staged: Staged[], drive: Record<number, DriveMinutes>): DemoState {
  if (staged.length === 0 && Object.keys(drive).length === 0) return state;
  const byVisitId = new Map(staged.filter((e) => e.kind === "visit").map((e) => [e.realVisitId, e]));
  const byBlockId = new Map(staged.filter((e) => e.kind === "block").map((e) => [e.blockId, e]));
  return {
    ...state,
    server: {
      ...state.server,
      visitResources: state.server.visitResources.map((vr) => {
        const entry = byVisitId.get(vr.visitId);
        const minutes = drive[vr.id];
        if (!entry && !minutes) return vr;
        return {
          ...vr,
          ...(entry ? { startIso: entry.startIso, endIso: entry.endIso } : {}),
          ...(minutes
            ? {
                startOfShiftDriveMinutes: minutes.startOfShiftDriveTime,
                interVisitDriveMinutes: minutes.interVisitDriveTime,
                endOfShiftDriveMinutes: minutes.endOfShiftDriveTime,
              }
            : {}),
        };
      }),
      blocks: state.server.blocks.map((block) => {
        const entry = byBlockId.get(block.id);
        return entry ? { ...block, startIso: entry.startIso, endIso: entry.endIso } : block;
      }),
    },
  };
}

/**
 * What the day reads on the server, spelled so a change to it can be told apart
 * from a poll that brought the same rows back.
 *
 * Two things hang off it. Pricing re-runs when the day underneath the staged
 * arrangement actually changes — somebody else moved a visit, a cancel landed —
 * but **not** on the poll that brings the identical rows every few seconds, which
 * would be a paid Google call a poll. And **Save** will only spend a price that
 * was taken against the day the server still has; otherwise it prices the batch
 * itself rather than writing minutes worked out for a day that no longer exists.
 */
function dayFingerprint(server: DemoState["server"], date: DateKey, tz: string): string {
  const onDay = (iso: string) => toDateKey(new Date(iso), tz) === date;
  const parts = [
    ...server.visitResources
      .filter((vr) => onDay(vr.startIso))
      .map((vr) => `v${vr.id}:${vr.startIso}-${vr.endIso}-${vr.startOfShiftDriveMinutes}/${vr.interVisitDriveMinutes}/${vr.endOfShiftDriveMinutes}-${vr.eventStatus}`),
    ...server.blocks.filter((b) => onDay(b.startIso)).map((b) => `b${b.id}:${b.startIso}-${b.endIso}-${b.canceled ? "x" : "o"}`),
  ];
  return parts.sort().join("|");
}

/** A price, and the two things it was taken against. Both have to still hold for Save to spend it. */
interface PricedDay {
  plan: DriveTimePlan;
  stagedKey: string;
  dayKey: string;
}

interface Dragging {
  card: WeekCard;
  /** Minutes between the top of the card and where she took hold of it. */
  grabOffset: number;
  durationMinutes: number;
}

interface Hover {
  start: number;
  /** A visit is checked against the patient's window; a block has no patient to check. */
  evaluation?: RealMoveEvaluation;
  /** What this drop would land across, phrased for the strip. It does not stop the drop. */
  overlap?: string;
}

export function MyDayDialog({ date, onClose }: { date: DateKey; onClose: () => void }) {
  const state = useDemoState();
  const now = useNow(30_000);
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const today = toDateKey(selectDemoNow(state, now), tz);
  // Whose day this is comes from the signed-in clinician, exactly as My week:
  // the panel is handed a date and nothing else.
  const providerId = useCurrentOrg()?.providerId ?? undefined;
  const bindings = state.meta.bindings.providerIdsByHandle;
  const bound = Object.keys(bindings).length > 0;
  const handle = useMemo(() => Object.entries(bindings).find(([, id]) => id === providerId)?.[0] ?? "", [bindings, providerId]);
  const clinician = state.roster[handle];

  const [staged, setStaged] = useState<Record<string, Staged>>({});
  /** What the staged arrangement drives, once the pricing call has answered. */
  const [priced, setPriced] = useState<PricedDay>();
  const [pricing, setPricing] = useState(false);

  // Everything below reads the staged day, so the axis, the overlap the strip
  // names and the guardrail all judge a drop against where the other staged cards
  // are now.
  const draft = useMemo(() => stagedState(state, Object.values(staged), priced?.plan.next ?? {}), [state, staged, priced]);

  const day = useMemo(() => selectMyDay(draft, handle, date, now), [draft, handle, date, now]);
  // The confirmation round is about the next working day and no other, so the
  // panel, the brackets and the chips are on that day and no other. `selectConfirmationRows` will happily
  // compute a window for any day it is asked about; being asked is the gate.
  const isTomorrow = date === addWorkingDaysToKey(today, 1);
  const roundRows = useMemo(() => (isTomorrow ? selectConfirmationRows(draft, providerId, date) : []), [isTomorrow, draft, providerId, date]);
  const roundByVisit = useMemo(() => new Map(roundRows.map((r) => [r.realVisitId, r])), [roundRows]);

  const [dragging, setDragging] = useState<Dragging>();
  /**
   * The card a single click has selected, and its popover — the same one My week
   * opens (Pete, 2026-10-08). Picking any card up closes it.
   */
  const [inspected, setInspected] = useState<{ card: WeekCard; anchor: HTMLElement }>();
  const closeInspect = useCallback(() => setInspected(undefined), []);
  const onSelectCard = useCallback((card: WeekCard, anchor: HTMLElement) => {
    setInspected((current) => (current?.card.id === card.id ? undefined : { card, anchor }));
  }, []);
  const startDrag = useCallback((drag: Dragging) => {
    setInspected(undefined);
    setDragging(drag);
  }, []);
  const [hover, setHover] = useState<Hover>();
  const [pendingMove, setPendingMove] = useState<{ card: WeekCard; start: number; durationMinutes: number; evaluation: RealMoveEvaluation }>();
  const [blockAnchor, setBlockAnchor] = useState<HTMLElement>();
  const [cancelFor, setCancelFor] = useState<WeekCard>();
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [message, setMessage] = useState<string>();
  const lanesRef = useRef<HTMLDivElement | null>(null);

  /**
   * The staged changes that still have a card on the day — the ones the header
   * counts and Save writes. A visit cancelled out from under a staged re-time
   * leaves its entry behind in the map; it is simply no longer one of these.
   */
  const pendingStops = day.stops.filter((stop) => staged[stop.card.id] !== undefined);
  const pendingCount = pendingStops.length;
  const pendingLabel = `${pendingCount} change${pendingCount === 1 ? "" : "s"} pending`;

  /** The arrangement, spelled so a change to it can be told from a re-render. */
  const stagedKey = Object.values(staged)
    .map((entry) => `${entry.cardId}@${entry.startIso}/${entry.endIso}`)
    .sort()
    .join("|");
  /**
   * The day the arrangement sits on. Pricing is keyed on these two and nothing
   * else: a horizon poll rewrites `state.server` every few seconds, and keying on
   * the object would be a paid Google call a poll, but a poll that genuinely
   * changes the day is a different day to price.
   */
  const serverDayKey = useMemo(() => dayFingerprint(state.server, date, tz), [state.server, date, tz]);
  // The rows the price is worked out from are read at call time, not closed over,
  // so pricing never runs on a stale horizon and never re-runs because of a poll.
  const serverRef = useRef(state.server);
  serverRef.current = state.server;
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  const priceSeq = useRef(0);

  useEffect(() => {
    if (stagedKey === "") {
      setPriced(undefined);
      setPricing(false);
      return;
    }
    const entries = Object.values(stagedRef.current);
    const server = serverRef.current;
    const moves: PlannedMove[] = [];
    for (const entry of entries) {
      if (entry.kind !== "visit") continue;
      const vr = server.visitResources.find((v) => v.visitId === entry.realVisitId);
      if (!vr) continue;
      moves.push({ visitResourceId: vr.id, fromProviderId: vr.providerId, fromStartIso: vr.startIso, fromEndIso: vr.endIso, toStartIso: entry.startIso, toEndIso: entry.endIso });
    }
    const blockMoves: PlannedBlockMove[] = entries.filter((entry) => entry.kind === "block" && entry.blockId !== undefined).map((entry) => ({ calendarBlockId: entry.blockId!, toStartIso: entry.startIso, toEndIso: entry.endIso }));
    if (moves.length === 0 && blockMoves.length === 0) return;
    // The latest arrangement wins. An answer to a question about an arrangement
    // she has already dragged past is dropped rather than drawn over the new one.
    const seq = ++priceSeq.current;
    setPricing(true);
    void priceStagedDay(moves, blockMoves)
      .then((plan) => {
        if (priceSeq.current !== seq) return;
        // An empty plan is a refused or timed-out call, which M2 answers by
        // keeping the minutes the day had rather than inventing any. Holding the
        // previous plan does the same thing for the previous arrangement.
        if (Object.keys(plan.next).length > 0) setPriced({ plan, stagedKey, dayKey: serverDayKey });
        setPricing(false);
      })
      .catch(() => {
        if (priceSeq.current !== seq) return;
        setPricing(false);
      });
    // These two are the whole of the input: the rows come from the refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedKey, serverDayKey]);

  /** Forget a card's staged change — it has been saved, discarded, or is no longer there to move. */
  const unstage = useCallback((cardId: string) => {
    setStaged((prev) => {
      if (prev[cardId] === undefined) return prev;
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
  }, []);

  /**
   * A drop, staged. One drop, two kinds of stop — a visit and a block still write
   * through different mutations at Save — but neither writes here: the card lands
   * where she let it go and the change waits with the others. Dragging a card back
   * to the minute the server has it clears its entry, so a change made and undone
   * by hand leaves nothing behind to save.
   */
  const stageMove = useCallback(
    (card: WeekCard, startMinutes: number, durationMinutes: number) => {
      const start = dateKeyAt(date, hhmmOfMinutes(startMinutes), tz);
      const end = new Date(start.getTime() + Math.max(MIN_STOP_MINUTES, durationMinutes) * 60_000);
      setStaged((prev) => {
        const serverStart = prev[card.id]?.serverStart ?? minutesOfHhmm(card.start);
        if (startMinutes === serverStart) {
          if (prev[card.id] === undefined) return prev;
          const next = { ...prev };
          delete next[card.id];
          return next;
        }
        return {
          ...prev,
          [card.id]: {
            cardId: card.id,
            kind: card.kind === "block" ? "block" : "visit",
            realVisitId: card.realVisitId,
            blockId: card.blockId,
            startIso: start.toISOString(),
            endIso: end.toISOString(),
            serverStart,
          },
        };
      });
    },
    [date, tz],
  );

  const onVisitCanceled = useCallback(
    async (card: WeekCard) => {
      if (card.realVisitId === undefined) return;
      // A visit that is gone has no hour left to argue about.
      unstage(card.id);
      try {
        await recordVisitCanceled(card.realVisitId);
        setMessage(`${card.title} canceled`);
      } catch (error) {
        setMessage(`Could not read the cancel back — ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [unstage],
  );

  const performRemoveBlock = useCallback(
    async (card: WeekCard) => {
      if (card.blockId === undefined) return;
      unstage(card.id);
      try {
        await removeBlockFromWeek(card.blockId);
        setMessage(`${card.title} removed`);
      } catch (error) {
        setMessage(`Could not remove the block — ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [unstage],
  );

  /** Where on the axis the pointer is, in minutes, once the grab offset is taken off. */
  const startAt = (clientY: number, drag: Dragging): number => {
    const rect = lanesRef.current?.getBoundingClientRect();
    const offsetPx = rect ? clientY - rect.top : 0;
    const raw = day.axisStart + offsetPx / PX_PER_MINUTE - drag.grabOffset;
    const snapped = snapToGrid(raw);
    return Math.max(day.axisStart, Math.min(day.axisEnd - drag.durationMinutes, snapped));
  };

  const evaluateAt = (drag: Dragging, start: number): Hover => {
    // A block has no patient, so there is no window, frequency or period to
    // read. What it does get, like a visit, is what it would land across.
    const evaluation =
      drag.card.realVisitId === undefined
        ? undefined
        : evaluateRealVisitMove(draft, drag.card.realVisitId, date, undefined, now, {
            start: hhmmOfMinutes(start),
            end: hhmmOfMinutes(start + drag.durationMinutes),
          });
    const overlapping = describeOverlap(stopsOverlapping(day, start, start + drag.durationMinutes, drag.card.id));
    const overlap = overlapping && clock.sentence(overlapping);
    return { start, evaluation, overlap };
  };

  const onDragOverAxis = (clientY: number) => {
    if (!dragging) return;
    const start = startAt(clientY, dragging);
    if (hover?.start === start) return;
    setHover(evaluateAt(dragging, start));
  };

  const onDropOnAxis = (clientY: number) => {
    const drag = dragging;
    setDragging(undefined);
    setHover(undefined);
    if (!drag) return;
    const start = startAt(clientY, drag);
    if (start === minutesOfHhmm(drag.card.start)) return;
    // A drop onto another stop's span lands (Pete, 2026-09-19). The strip named
    // what it would land across while she was holding it; nothing is asked here
    // and nothing is prevented, and the lane machinery draws the two side by side
    // exactly as it draws the pair the seed already contains.
    const { evaluation } = evaluateAt(drag, start);
    if (evaluation?.level === "block") return;
    if (evaluation?.level === "warn") {
      setPendingMove({ card: drag.card, start, durationMinutes: drag.durationMinutes, evaluation });
      return;
    }
    stageMove(drag.card, start, drag.durationMinutes);
  };

  /**
   * **Save.** Everything staged, written at once. The panel lets go of the staged
   * changes only when the write has landed, so a refusal leaves them on the axis
   * to try again.
   */
  const onSave = async () => {
    const changes: StagedRetime[] = pendingStops.map((stop) => {
      const entry = staged[stop.card.id];
      return { kind: entry.kind, realVisitId: entry.realVisitId, blockId: entry.blockId, startIso: entry.startIso, endIso: entry.endIso };
    });
    setSaving(true);
    try {
      // The arrangement has already been priced; Save takes that answer rather
      // than paying Google a second time for it — but only if the price was taken
      // against this arrangement *and* against the day the server still has. If a
      // move landed from somewhere else in between, Save prices the batch itself
      // rather than writing minutes worked out for a day that has gone.
      const usable = priced && priced.stagedKey === stagedKey && priced.dayKey === serverDayKey ? priced.plan : undefined;
      const saved = await saveDayRetimes(changes, usable);
      setStaged({});
      setPriced(undefined);
      setMessage(`${saved} change${saved === 1 ? "" : "s"} saved`);
    } catch (error) {
      setMessage(`Could not save — ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSaving(false);
    }
  };

  /** **Reset.** The staged changes go and nothing else: nothing was ever written. */
  const onResetStaged = () => {
    setStaged({});
    setPriced(undefined);
    setMessage(pendingCount === 1 ? "1 pending change discarded" : `${pendingCount} pending changes discarded`);
  };

  /**
   * Closing with changes still staged asks first. The staged day lives in this
   * component and nothing else, so closing the panel is what would lose it — the
   * alternative, a slice in the store that survives the unmount, is demo state
   * that a rehearsal would have to remember to clear. One question is cheaper and
   * cannot silently drop her work. The browser's **back** button unmounts the
   * panel without passing through here; that is the one exit the question misses.
   */
  const requestClose = () => {
    if (pendingCount > 0) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  // `drop` only fires over the lanes, so a card released anywhere else — the
  // gutter, the tray, Escape — would otherwise leave the strip on screen for the
  // rest of the act. `dragend` fires on the source after any release.
  const onDragEnd = () => {
    setDragging(undefined);
    setHover(undefined);
  };

  /**
   * **Mark confirmed** (3.3): she called the patient. It is a store write against
   * the day the *server* has, which is why the menu item is disabled while the
   * card is staged — `CardMenu` says *Save the day first*.
   */
  const markConfirmed = (card: WeekCard) => {
    if (card.realVisitId === undefined) return;
    confirmByPhoneNow(card.realVisitId);
    setMessage(`${card.title} confirmed`);
  };

  const onBlockCreated = (blockId: number, block: Parameters<typeof recordBlockCreated>[1]) => {
    void recordBlockCreated(blockId, block).catch((error: unknown) => setMessage(`Could not read the new block back — ${error instanceof Error ? error.message : String(error)}`));
  };

  const dayLabel = `${day.weekdayLabel} ${day.dateLabel}`;
  const canSequence = day.stops.filter((s) => s.card.kind === "visit").length > 1;
  const height = (day.axisEnd - day.axisStart) * PX_PER_MINUTE;
  const top = (minutes: number) => (minutes - day.axisStart) * PX_PER_MINUTE;

  if (!bound) return null;
  if (!clinician || providerId === undefined) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth sx={PANEL_Z}>
        <DialogTitle>My day</DialogTitle>
        <DialogContent>
          <Alert severity="info" variant="outlined">
            My day shows a day of the clinician you are signed in as. This sign-in is not one of the branch&apos;s clinicians — choose one from the user menu at the top right.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open onClose={requestClose} maxWidth="md" fullWidth scroll="paper" disableEnforceFocus sx={PANEL_Z} PaperProps={{ sx: PANEL_PAPER }}>
        <DialogTitle sx={{ py: 1.5, flex: "0 0 auto" }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: "wrap" }}>
            <Typography variant="h6" fontWeight={600} component="span">
              {formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "EEEE, MMMM d")}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" component="span">
              {clinician.displayName}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* What is waiting, and the two ways out of it. Both sit in the header,
                which the frame keeps on screen however far down the day she is. */}
            {pendingCount > 0 && <Chip size="small" color="warning" variant="outlined" label={pendingLabel} data-testid="pending-count" sx={{ borderRadius: "4px" }} />}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" onClick={onResetStaged} disabled={pendingCount === 0 || saving}>
                Reset
              </Button>
              <Button size="small" variant="contained" onClick={() => void onSave()} disabled={pendingCount === 0 || saving}>
                Save
              </Button>
            </Stack>
            {!day.isPast && (
              <DayMenu
                label={dayLabel}
                // Optimize reads the day off the server and writes straight over
                // it, so with changes staged it would fight them. It comes back
                // the moment they are saved or reset.
                canSequence={canSequence && pendingCount === 0}
                onAddBlock={setBlockAnchor}
                onSequence={() => setSequenceOpen(true)}
                onViewRoute={() => setRouteOpen(true)}
              />
            )}
            <IconButton size="small" aria-label="Close" onClick={requestClose}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers onDragEnd={onDragEnd} data-testid="my-day-panel" sx={PANEL_BODY}>
          {dragging && hover && (
            <GuardrailStrip patientName={dragging.card.title} targetLabel={clock.wall(hhmmOfMinutes(hover.start))} evaluation={hover.evaluation} overlap={hover.overlap} stickyTop={0} />
          )}

          {day.tray.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="needs-a-time-tray">
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Needs a time
              </Typography>
              {/* A reservation is placed into free time when it is created, so
                  the tray is what is left when there was none: on the day, with
                  no hour the search could find. It takes no room on the axis,
                  and the branch's worklist carries the same thing as a row. */}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                On the day, with no free span to put it in. Nothing here takes room on the axis.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                {day.tray.map((card) => (
                  <Paper
                    key={card.id}
                    variant="outlined"
                    data-card-kind={card.kind}
                    sx={{ p: 0.75, minWidth: 180, backgroundImage: HATCH, borderStyle: card.kind === "planned" ? "solid" : "dashed", ...(card.kind === "planned" ? { borderColor: "primary.main" } : {}) }}
                  >
                    <Typography variant="body2" fontWeight={500} component="div">
                      {card.title}
                    </Typography>
                    {card.subtitle && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {card.subtitle}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: "flex", position: "relative", height }}>
                {/* Hours on the left; the stops and the drive between them in the lanes. */}
                <Box sx={{ width: GUTTER_PX, position: "relative", flexShrink: 0 }}>
                  {hourLines(day).map((minutes) => (
                    <Typography key={minutes} variant="caption" color="text.secondary" sx={{ position: "absolute", top: top(minutes) - 8, right: 8 }}>
                      {clock.mark(hhmmOfMinutes(minutes))}
                    </Typography>
                  ))}
                </Box>
                <Box
                  ref={lanesRef}
                  data-testid="day-axis"
                  onDragOver={(e) => {
                    e.preventDefault();
                    onDragOverAxis(e.clientY);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    onDropOnAxis(e.clientY);
                  }}
                  sx={{ position: "relative", flex: 1, borderLeft: "1px solid", borderColor: "divider" }}
                >
                  {hourLines(day).map((minutes) => (
                    <Box key={minutes} sx={{ position: "absolute", top: top(minutes), left: 0, right: 0, borderTop: "1px solid", borderColor: "divider", opacity: 0.6 }} />
                  ))}
                  {day.stops.map((stop) => (
                    <AxisStop
                      key={stop.card.id}
                      stop={stop}
                      staged={staged[stop.card.id] !== undefined}
                      clinicianName={clinician.displayName}
                      dayLabel={dayLabel}
                      round={stop.card.realVisitId !== undefined ? roundByVisit.get(stop.card.realVisitId) : undefined}
                      top={top(stop.start)}
                      height={(stop.drawnEnd - stop.start) * PX_PER_MINUTE}
                      onDragStart={startDrag}
                      onSelect={onSelectCard}
                      selected={inspected?.card.id === stop.card.id}
                      onCancelVisit={setCancelFor}
                      onRemoveBlock={performRemoveBlock}
                      onMarkConfirmed={markConfirmed}
                      timeZone={tz}
                    />
                  ))}
                  {/* The drive, in the lane the stop it arrives at sits in and at that
                      lane's full width, because it is part of that stop's footprint
                      rather than a narrower thing threaded between cards. It takes no
                      pointer events, so a card dragged over it still reaches the axis
                      underneath. */}
                  {day.drives.map((segment) => (
                    <Box
                      key={segment.key}
                      data-drive={segment.stale ? "recomputing" : String(segment.minutes)}
                      data-drive-pricing={pricing ? "true" : undefined}
                      data-lane={`${segment.lane + 1} of ${segment.laneCount}`}
                      title={segment.stale ? "Recomputing the route" : pricing ? "Repricing the route" : `${segment.minutes} min drive`}
                      style={laneBox(segment.lane, segment.laneCount, top(segment.start), Math.max(6, (segment.end - segment.start) * PX_PER_MINUTE))}
                      sx={{
                        position: "absolute",
                        boxSizing: "border-box",
                        pr: "4px",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        "& > *": { flexShrink: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          boxSizing: "border-box",
                          borderRadius: "3px",
                          border: "1px solid",
                          borderColor: DRIVE_BORDER_COLOR,
                          borderStyle: segment.stale ? "dashed" : "solid",
                          background: segment.stale ? undefined : DRIVE_STRIPES,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {/* While the price is in flight the minutes the day already
                            has are kept and dimmed, so the band never empties and
                            never changes shape: it settles back to the ordinary
                            treatment with the new number. */}
                        <Typography variant="caption" color="text.primary" noWrap sx={{ fontSize: 10, lineHeight: 1, opacity: pricing ? 0.5 : 1 }}>
                          {segment.stale ? "" : `${segment.minutes} min drive`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {day.points} points · drive {day.driveMinutes} min
            </Typography>
          </Paper>
        </DialogContent>
      </Dialog>

      {pendingMove && (
        <MoveAnywayDialog
          patientName={pendingMove.card.title}
          toLabel={clock.wall(hhmmOfMinutes(pendingMove.start))}
          evaluation={pendingMove.evaluation}
          onKeep={() => setPendingMove(undefined)}
          onMoveAnyway={() => {
            const move = pendingMove;
            setPendingMove(undefined);
            stageMove(move.card, move.start, move.durationMinutes);
          }}
        />
      )}
      {cancelFor?.realVisitId !== undefined && (
        <CancelVisitDialog
          visitId={cancelFor.realVisitId}
          onClose={() => setCancelFor(undefined)}
          onSuccess={() => {
            const canceled = cancelFor;
            setCancelFor(undefined);
            void onVisitCanceled(canceled);
          }}
        />
      )}
      <CalendarPopup isOpen={!!blockAnchor} visitAnchor={blockAnchor} onClose={() => setBlockAnchor(undefined)}>
        {blockAnchor && (
          <AddCalendarBlock
            key={date}
            date={dateKeyAt(date, "12:00", tz)}
            blockType={CalendarBlockBlockTypeEnum.Meeting}
            lockedProviderId={providerId}
            onCreated={onBlockCreated}
            onClose={() => setBlockAnchor(undefined)}
          />
        )}
      </CalendarPopup>
      {sequenceOpen && <SequenceDayDialog date={date} providerId={providerId} onClose={() => setSequenceOpen(false)} />}
      {/* Mounted only while she is looking at it, so the route view's location poll stops when she closes it. */}
      {routeOpen && <RouteDayDialog date={date} providerId={providerId} providerName={clinician.displayName} onClose={() => setRouteOpen(false)} />}
      {confirmClose && (
        <Dialog open onClose={() => setConfirmClose(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Close without saving?</DialogTitle>
          <DialogContent>
            <DialogContentText>{pendingLabel} on this day. Closing discards them; nothing already saved is affected.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
            <Button
              color="warning"
              onClick={() => {
                setConfirmClose(false);
                setStaged({});
                setPriced(undefined);
                onClose();
              }}
            >
              Discard and close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {inspected && <VisitInspectPopover card={inspected.card} anchorEl={inspected.anchor} clinicianName={clinician.displayName} onClose={closeInspect} />}
      <Snackbar open={!!message} autoHideDuration={6000} onClose={() => setMessage(undefined)} message={message} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </>
  );
}

/**
 * What the icon says on this view. The axis already puts the card at the hour it
 * is planned for, so the one thing the card cannot show — the window the text
 * actually named — goes here beside the state and its reasons.
 */
/**
 * One stop on the axis. The card reads as it does on My week — time, patient,
 * visit type, address on a block, confirmation chip — and carries the same
 * overflow menu; what is new is that it is sized by its planned duration, placed
 * in its lane, and, on the next working day, bracketed by the arrival window the
 * patient was offered.
 */
function AxisStop({
  stop,
  staged,
  clinicianName,
  dayLabel,
  round,
  top,
  height,
  onDragStart,
  onSelect,
  selected,
  onCancelVisit,
  onRemoveBlock,
  onMarkConfirmed,
  timeZone,
}: {
  stop: DayStop;
  /** This card carries a re-time that is waiting for Save. */
  staged: boolean;
  clinicianName: string;
  dayLabel: string;
  /** The confirmation round's row for this visit, on the day the round is about. */
  round?: ReturnType<typeof selectConfirmationRows>[number];
  top: number;
  height: number;
  onDragStart: (dragging: Dragging) => void;
  /** A single click on a visit opens its popover, as on My week. */
  onSelect: (card: WeekCard, anchor: HTMLElement) => void;
  selected: boolean;
  onCancelVisit: (card: WeekCard) => void;
  onRemoveBlock: (card: WeekCard) => void;
  onMarkConfirmed: (card: WeekCard) => void;
  /** The branch's zone: the window is the patient's local hour, not the laptop's. */
  timeZone: string;
}) {
  const { card } = stop;
  const cardWindow = useCardWindowLabel(timeZone);
  const clock = useBranchClock(timeZone);
  const [overMenu, setOverMenu] = useState(false);
  const inspectable = isInspectable(card);
  // A real visit and a block wear the command centre's own colours; a held or
  // reserved slot has no command-centre equivalent and keeps the demo's hatch.
  const surface = card.kind === "visit" ? SCHEDULED_VISIT : card.kind === "block" ? BLOCK_SURFACE : { bgcolor: "background.paper" };
  const hatch = card.hatched
    ? {
        backgroundImage: HATCH,
        borderStyle: card.kind === "planned" ? ("solid" as const) : ("dashed" as const),
        ...(card.kind === "planned" ? { borderColor: "primary.main" } : {}),
      }
    : {};
  return (
    <Box data-testid="axis-stop" style={laneBox(stop.lane, stop.laneCount, top, height)} sx={{ position: "absolute", pr: "4px", boxSizing: "border-box" }}>
      <Tooltip title={card.kind === "hold" ? `Held for a start of care on ${clinicianName}` : (card.address ?? "")} disableHoverListener={card.kind !== "hold" && !card.address}>
        <Paper
          variant="outlined"
          // `retimable`, not `draggable`: the week's drag takes a card to another
          // day, which a block does not do; this one moves it on its own day.
          // A staged card carries no overlay entry — the staged day rewrites the
          // horizon rows rather than laying `local` over them — so `retimable` is
          // still true for it and a staged card is still hers to pick up again.
          draggable={card.retimable && !overMenu}
          onDragStart={(e) => {
            if (card.realVisitId === undefined && card.blockId === undefined) return;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            onDragStart({ card, grabOffset: (e.clientY - rect.top) / PX_PER_MINUTE, durationMinutes: stop.end - stop.start });
          }}
          // A single click selects a visit and opens its popover; a click meant for
          // the card's menu, or anything the menu renders, is not one.
          onClick={(e) => {
            if (!inspectable) return;
            const target = e.target as HTMLElement;
            if (!e.currentTarget.contains(target) || target.closest("button")) return;
            onSelect(card, e.currentTarget);
          }}
          data-card-kind={card.kind}
          data-lane={`${stop.lane + 1} of ${stop.laneCount}`}
          // The hour the card actually sits at. On the axis that is a position
          // rather than text — the card itself reads the patient's arrival
          // window — so this is what a re-time is asserted against.
          data-start={card.start}
          data-staged={staged ? "true" : undefined}
          sx={{
            height: "100%",
            overflow: "hidden",
            p: 0.5,
            cursor: card.retimable ? "grab" : inspectable ? "pointer" : "default",
            ...surface,
            // A card in flight is dimmed; a staged one is not sent anywhere yet,
            // so it keeps its colour and takes the amber dashed edge instead.
            opacity: card.pending && !staged ? 0.7 : 1,
            ...hatch,
            ...(staged ? { borderStyle: "dashed", borderWidth: 2, borderColor: "warning.main" } : {}),
            ...(selected ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 } : {}),
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
            {/* The window the patient has, not the minute the clinician plans on
                (Pete, 2026-09-19: *"I was looking to replace the time range on the
                visit card itself with the arrival window"*) — the same string the
                week column shows, so one visit reads the same in both places. The
                axis still positions the card by its real start, so the planned
                minute is not lost: it is where the card **is**, rather than what
                it says. A card with no window — a block, a hold, a placeholder —
                reads its own span instead, phrased the same way. */}
            <Typography variant="caption" color="text.secondary" component="div">
              {cardWindow(card)}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
              {round && <ConfirmationIcon status={round.status} label={clock.sentence(round.label)} fontSize={14} />}
              {(card.removable || card.markConfirmable) && (
                <Box
                  onMouseEnter={() => setOverMenu(true)}
                  onMouseLeave={() => setOverMenu(false)}
                  onDragStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <CardMenu card={card} dayLabel={dayLabel} timeZone={timeZone} staged={staged} onCancelVisit={onCancelVisit} onRemoveBlock={onRemoveBlock} onMarkConfirmed={onMarkConfirmed} />
                </Box>
              )}
            </Stack>
          </Stack>
          <Typography variant="body2" fontWeight={500} component="div" noWrap>
            {card.title}
          </Typography>
          {card.subtitle && (
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              {card.subtitle}
            </Typography>
          )}
          {card.address && (
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              {card.address}
            </Typography>
          )}
        </Paper>
      </Tooltip>
    </Box>
  );
}
